import { useEffect, useRef, useState } from 'react'

export type HumanVerificationAction = 'department_create' | 'registration_apply' | 'login'

export type HumanVerificationConfig = {
  enabled: boolean
  provider: 'turnstile' | 'none'
  siteKey: string
  loginFailureThreshold: number
}

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileScriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (turnstileScriptPromise) return turnstileScriptPromise
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-buwuyun-turnstile]')
    const ready = () => window.turnstile ? resolve() : reject(new Error('Turnstile did not initialize'))
    if (existing) {
      existing.addEventListener('load', ready, { once: true })
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.buwuyunTurnstile = 'true'
    script.addEventListener('load', ready, { once: true })
    script.addEventListener('error', () => reject(new Error('Turnstile failed to load')), { once: true })
    document.head.appendChild(script)
  })
  return turnstileScriptPromise
}

export default function HumanVerification({
  enabled,
  siteKey,
  action,
  resetKey,
  onChange
}: {
  enabled: boolean
  siteKey: string
  action: HumanVerificationAction
  resetKey: number
  onChange: (token: string) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const onChangeRef = useRef(onChange)
  const [status, setStatus] = useState<'loading' | 'ready' | 'expired' | 'error'>('loading')

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    if (!enabled || !siteKey || !containerRef.current) return
    let disposed = false
    let widgetId = ''
    onChangeRef.current('')
    setStatus('loading')
    void loadTurnstileScript()
      .then(() => {
        if (disposed || !containerRef.current || !window.turnstile) return
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          language: 'zh-cn',
          theme: 'light',
          size: 'flexible',
          callback: (token: string) => {
            setStatus('ready')
            onChangeRef.current(token)
          },
          'expired-callback': () => {
            setStatus('expired')
            onChangeRef.current('')
          },
          'timeout-callback': () => {
            setStatus('expired')
            onChangeRef.current('')
          },
          'error-callback': () => {
            setStatus('error')
            onChangeRef.current('')
          }
        })
      })
      .catch(() => {
        if (!disposed) setStatus('error')
      })
    return () => {
      disposed = true
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [action, enabled, resetKey, siteKey])

  if (!enabled) return null
  return <div className="human-verification">
    <div ref={containerRef} className="human-verification-widget" />
    {status === 'loading' && <small>正在加载安全验证…</small>}
    {status === 'ready' && <small className="success-copy">验证已通过。</small>}
    {status === 'expired' && <small className="error-copy">验证已失效，请重新完成验证。</small>}
    {status === 'error' && <small className="error-copy">安全验证加载失败，请检查网络后刷新页面。</small>}
  </div>
}
